import React, { useState } from 'react';
import {Button, CircularProgress, TextField, Typography} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../index.css';
import { useTheme } from '../contexts/ThemeContext';
import SEOHead from './SEOHead';

function Delete() {
 const [email, setEmail] = useState('');
 const [otp, setOtp] = useState('');
 const [otpResponse, setOtpResponse] = useState({});
 const [progress, setProgress] = useState(0);
 const [otpButtonEnabled, setOtpEnabled] = useState(true);
 const { colors, isDark } = useTheme();

 const api_endpoint = "https://notes-fast-2ciujxlp5q-uc.a.run.app"

 const handleSubmit = (e) => {
    e.preventDefault();
    fetch(api_endpoint + '/account/send_otp?email='+email, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((res) =>{
          if (res.status !== 200){
              toast.error(res.statusText);
          }
          return res.json()})
      .then((data) => {
        if (data.status === "success"){
            setOtpEnabled(false);
            toast.success(data.message);
        }else{
            toast.error(data.message);
        }
        setOtpResponse(data);
      })
      .catch((error) => {
          toast.error("Limit exceeded, Try after an Hour");
        console.error('Error:', error);
      });
 };

 const handleDeleteAccount = (e) => {
     e.preventDefault();
     setProgress(1);
    fetch(api_endpoint + '/account/delete?otp='+otp+'&email='+email, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
          if (res.status !== 200){
              toast.error(res.statusText);
          }
          return res.json();})
      .then((data) => {
          if (data.status === "success"){
            toast.success(data.message);
            setOtpResponse({});
        }else{
            toast.error(data.message);
        }
          setProgress(0);
      })
      .catch((error) => {
        console.error('Error:', error);
        setProgress(0);
      });
 };

 const handleInputChange = (event) => {
    // Allow only numeric input
    const numericInput = event.target.value.replace(/\D/g, '');
    const limitedInput = numericInput.slice(0, 6);
    setOtp(limitedInput);
  };

 return (
    <>
    <SEOHead
        title="Delete Your Collegia Account"
        description="How to delete your Collegia account and all associated data. Step-by-step guide for account removal from Collegia: Academics Manager."
        keywords="delete Collegia account, remove account, Collegia data deletion, account removal, privacy"
        url="https://collegia.app/deleteAccount"
    />
    <div className={`${colors.bg} min-h-screen py-8 px-4`}>
      <div className="max-w-2xl mx-auto flex flex-col">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mb-6">
            <span className="text-2xl text-red-500">🗑️</span>
          </div>
          <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${colors.text}`}>
            Delete Your Collegia Account
          </h1>
          <p className={`text-lg ${colors.textSecondary} font-light`}>
            We're sorry to see you go. This guide specifically covers deleting your account in&nbsp;
            <span className={`${colors.text} font-semibold`}>Collegia: Academics Manager</span> — a privacy-first app crafted by&nbsp;
            <span className={`${colors.text} font-semibold`}>Collegia</span>. Please read the details below before proceeding.
          </p>
        </div>

        {/* Important Clarification */}
        <div className={`${colors.card} rounded-2xl p-6 border border-blue-500/30 backdrop-blur-sm mb-8 bg-blue-500/5`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
              <span className="text-blue-500">ℹ️</span>
            </div>
            <div>
              <h3 className={`text-xl font-semibold mb-3 ${colors.text}`}>
                Important: What Data We Actually Store
              </h3>
              <div className={`${colors.textSecondary} space-y-3`}>
                <p>
                  <strong className="text-blue-400">Good news:</strong> With <span className="font-semibold">Collegia</span>, we don't store your university data, credentials, or academic information.&nbsp;
                  Everything processes locally on your device.
                </p>
                <p>
                  Account deletion only removes:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Your app authentication data (if using Google Sign-In)</li>
                  <li>App preferences and settings</li>
                  <li>Any support communication history</li>
                  <li>Technical usage logs (no personal content)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy First Reminder */}
        <div className={`${colors.card} rounded-2xl p-6 border border-green-500/30 backdrop-blur-sm mb-8 bg-green-500/5`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
              <span className="text-green-500">🔒</span>
            </div>
            <div>
              <h3 className={`text-xl font-semibold mb-3 ${colors.text}`}>
                Your Academic Data Stays Private
              </h3>
              <div className={`${colors.textSecondary} space-y-2`}>
                <p>
                  Since we process everything locally on your device:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Your university credentials are never stored on our servers</li>
                  <li>Your grades, schedules, and academic records remain only on your device</li>
                  <li>No university data needs to be deleted - it was never collected</li>
                  <li>Simply uninstalling the app removes all locally stored information</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Warning Section */}
        <div className={`${colors.card} rounded-2xl p-6 border border-red-500/30 backdrop-blur-sm mb-8 bg-red-500/5`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
              <span className="text-red-500">⚠️</span>
            </div>
            <div>
              <h3 className={`text-xl font-semibold mb-3 ${colors.text} text-red-400`}>
                What Account Deletion Will Do
              </h3>
              <div className={`${colors.textSecondary} space-y-2`}>
                <p>• Remove your Google Sign-In authentication (if used)</p>
                <p>• Delete app preferences and personalized settings</p>
                <p>• Remove access to any cloud-synced app features</p>
                <p>• This action is <strong className="text-red-400">permanent and cannot be undone</strong></p>
              </div>
            </div>
          </div>
        </div>

        {/* Alternative Options */}
        <div className={`${colors.card} rounded-2xl p-6 border ${colors.border} backdrop-blur-sm mb-8`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
              <span className="text-purple-500">💡</span>
            </div>
            <div>
              <h3 className={`text-xl font-semibold mb-3 ${colors.text}`}>
                Consider These Alternatives
              </h3>
              <div className={`${colors.textSecondary} space-y-3`}>
                <p>
                  Since your academic data is processed locally, you might consider:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Simply uninstalling the app</strong> - removes all local data without formal deletion</li>
                  <li><strong>Clearing app data</strong> - resets the app while keeping your account</li>
                  <li><strong>Logging out</strong> - stops data processing while preserving your account</li>
                  <li><strong>Taking a break</strong> - the app will be here when you return</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Step Process */}
        <div className="space-y-8 order-first">
          {/* Step 1: Email Verification */}
          <div className={`${colors.card} rounded-2xl p-8 pb-12 border ${colors.border} backdrop-blur-sm ${otpResponse.status === "success" ? 'opacity-60' : ''}`}>
            <div className="flex items-center mb-6">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 ${otpResponse.status === "success" ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'}`}>
                {otpResponse.status === "success" ? '✓' : '1'}
              </div>
              <h3 className={`text-2xl font-semibold ${colors.text}`}>
                Use email to delete your account
              </h3>
            </div>
            
            <p className={`${colors.textSecondary} mb-6`}>
              Enter your registered email address to receive a verification code for account deletion.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <TextField
                  label="Email Address"
                  placeholder="Enter your email address"
                  variant="outlined"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={otpResponse.status === "success"}
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                      color: isDark ? 'white' : 'black',
                      '& fieldset': {
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                      },
                      '& input': {
                        color: isDark ? 'white !important' : 'black !important',
                        '&::placeholder': {
                          color: isDark ? 'rgba(255, 255, 255, 0.5) !important' : 'rgba(0, 0, 0, 0.5) !important',
                          opacity: '1 !important',
                        },
                        '&:-webkit-autofill': {
                          WebkitBoxShadow: isDark ? '0 0 0 1000px rgba(255, 255, 255, 0.05) inset' : '0 0 0 1000px rgba(0, 0, 0, 0.02) inset',
                          WebkitTextFillColor: isDark ? 'white !important' : 'black !important',
                        },
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                      '&.Mui-focused': {
                        color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
                      },
                    },
                  }}
                />
              </div>
              
              {otpResponse.status !== "success" && (
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={!otpButtonEnabled || !email}
                  sx={{
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    color: 'white',
                    fontWeight: 'bold',
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '16px',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1976D2 30%, #0288D1 90%)',
                    },
                    '&:disabled': {
                      background: 'rgba(128, 128, 128, 0.3)',
                      color: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                >
                  📧 Send Verification Code
                </Button>
              )}
            </form>
          </div>

          {/* Step 2: OTP Verification & Account Deletion */}
          {otpResponse && otpResponse.status === "success" && (
            <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm`}>
              <div className="flex items-center mb-6">
                <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center mr-4">
                  2
                </div>
                <h3 className={`text-2xl font-semibold ${colors.text}`}>
                  Final Confirmation
                </h3>
              </div>
              
              <p className={`${colors.textSecondary} mb-6`}>
                Enter the 6-digit verification code sent to your email to confirm account deletion.
              </p>

              <form onSubmit={handleDeleteAccount} className="space-y-4">
                <div>
                  <TextField
                    label="Verification Code"
                    variant="outlined"
                    type="text"
                    value={otp}
                    onChange={handleInputChange}
                    placeholder="000000"
                    fullWidth
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                        color: isDark ? 'white' : 'black',
                        '& fieldset': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                        },
                        '& input': {
                          textAlign: 'center',
                          fontSize: '24px',
                          letterSpacing: '8px',
                          fontWeight: 'bold',
                          color: isDark ? 'white !important' : 'black !important',
                          '&::placeholder': {
                            color: isDark ? 'rgba(255, 255, 255, 0.5) !important' : 'rgba(0, 0, 0, 0.5) !important',
                            opacity: '1 !important',
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                        '&.Mui-focused': {
                          color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
                        },
                      },
                    }}
                    inputProps={{ 
                      inputMode: 'numeric', 
                      pattern: '[0-9]*',
                      maxLength: 6,
                    }}
                  />
                </div>
                
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={otp.length !== 6 || progress !== 0}
                  sx={{
                    background: 'linear-gradient(45deg, #f44336 30%, #e91e63 90%)',
                    color: 'white',
                    fontWeight: 'bold',
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '16px',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #d32f2f 30%, #c2185b 90%)',
                    },
                    '&:disabled': {
                      background: 'rgba(128, 128, 128, 0.3)',
                      color: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                >
                  {progress !== 0 ? '🔄 Deleting Account...' : '🗑️ Delete Account Permanently'}
                </Button>
              </form>

              {progress !== 0 && (
                <div className="flex items-center justify-center mt-6">
                  <CircularProgress 
                    size={40} 
                    thickness={4} 
                    sx={{ 
                      color: '#f44336',
                      mr: 2 
                    }} 
                  />
                  <span className={`${colors.textSecondary}`}>Processing deletion...</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="text-center mt-12 pt-8 border-t border-gray-700/20">
          <h3 className={`text-xl font-semibold mb-4 ${colors.text}`}>
            Questions About Data Privacy? 🤔
          </h3>
          <p className={`${colors.textSecondary} mb-6`}>
            Learn more about our privacy-first approach and what data we actually handle.
          </p>
          <div className="flex justify-center space-x-4">
            <a 
              href="/privacy_policy"
              className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
            >
              🔒 Privacy Policy
            </a>
            <a 
              href="/university_response"
              className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
            >
              🤝 Our Approach
            </a>
            <a 
              href="mailto:contact@collegia.app"
              className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
            >
              📧 Contact Support
            </a>
            <a 
              href="/"
              className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
            >
              🏠 Back to Home
            </a>
          </div>
        </div>

        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={isDark ? "dark" : "light"}
          style={{
            fontSize: '16px',
          }}
        />
      </div>
    </div>
    </>
 );
}

export default Delete;
